<template>
    <div class="mentions-popover">
        <mentions-pane-categories
            ref="categories"
            :active-category="categoryActive"
            :categories="categories"
            :count-by-category="countByCategory"
            :focused="activePane === 'categories'"
            :loading-state-by-category="loadingStateByCategory"
            :prevent-disabling-categories="['account', 'visitor']"
            @enter="onCategoryEnter"
            @focus="selectPane('categories')"
            @select="onCategorySelect" />
        <mentions-pane-results
            ref="results"
            :category="categoryActive"
            :focused="activePane === 'results'"
            :inactive-visible-by-category="inactiveVisibleByCategory"
            :items="visibleItems"
            :loading-state="loadingStateByCategory[categoryActive]"
            :show-labels="categoryActive === 'all'"
            :show-limited-results-notice="isActiveCategoryLimitedResults"
            @focus="selectPane('results')"
            @select="onSelectItem"
            @toggle-inactive="onToggleInactive" />
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import MentionsPaneCategories from '@/ask-pendo/components/mentions-popover/mentions-pane-categories.vue';
import MentionsPaneResults from '@/ask-pendo/components/mentions-popover/mentions-pane-results.vue';
import { getItemText, itemFilter, normalizeItem } from '@/ask-pendo/components/mentions-popover/items.utils';
import { LOADING_STATES } from '@/constants';
import MentionsSearchAccount from '@/ask-pendo/components/mentions-popover/mentions-search-account.aggregation';
import MentionsSearchVisitor from '@/ask-pendo/components/mentions-popover/mentions-search-visitor.aggregation';
import { connectAggregationToState, withComponent } from '@pendo/agg-connect';
import without from 'lodash/without';
import every from 'lodash/every';

const { LOADING, RESOLVED } = LOADING_STATES;

export default {
    name: 'MentionsPopover',
    components: {
        MentionsPaneCategories,
        MentionsPaneResults,
    },
    props: {
        command: {
            type: Function,
            required: true,
        },
        query: {
            type: String,
            required: true,
        },
        setOnKeyDownHandler: {
            type: Function,
            required: true,
        },
    },
    data () {
        return {
            categories: ['all', 'account', 'app', 'feature', 'page', 'productArea', 'segment', 'trackEvent', 'visitor'],
            categoryActive: 'all',
            filteredItemsByCategory: {
                account: [],
                visitor: [],
            },
            loadingStateByCategory: {
                all: RESOLVED,
                account: RESOLVED,
                app: RESOLVED,
                feature: RESOLVED,
                page: RESOLVED,
                productArea: RESOLVED,
                segment: RESOLVED,
                trackEvent: RESOLVED,
                visitor: RESOLVED,
            },
            inactiveVisibleByCategory: {
                account: false,
                visitor: false,
            },
            panes: ['categories', 'results'],
            paneIndexActive: 1,
            subscriptionAccountSearch: null,
            subscriptionVisitorSearch: null,
        };
    },
    computed: {
        ...mapGetters({
            apps: 'apps/list',
            features: 'features/list',
            groups: 'groups/list',
            pages: 'pages/list',
            segments: 'segments/list',
            trackEvents: 'trackTypes/list',
        }),
        activePane () {
            if (!this.visibleItems.length) {
                return this.panes[0];
            }

            return this.panes[this.paneIndexActive];
        },
        activePaneRef () {
            switch(this.activePane) {
                case 'categories':
                    return this.$refs.categories;
                case 'results':
                    return this.$refs.results;
            }

            return null;
        },
        allApps () {
            return this.apps.map(normalizeItem('app'));
        },
        allGroups () {
            return this.groups.map(normalizeItem('productArea'));
        },
        allFeatures () {
            return this.features.map(normalizeItem('feature'));
        },
        allPages () {
            return this.pages.map(normalizeItem('page'));
        },
        allSegments () {
            return this.segments.map(normalizeItem('segment'));
        },
        allTrackEvents () {
            return this.trackEvents.map(normalizeItem('trackEvent'));
        },
        countByCategory () {
            return {
                all: this.filteredAll.length,
                account: this.filteredItemsByCategory.account.length,
                app: this.filteredApps.length,
                feature: this.filteredFeatures.length,
                page: this.filteredPages.length,
                productArea: this.filteredGroups.length,
                segment: this.filteredSegments.length,
                trackEvent: this.filteredTrackEvents.length,
                visitor: this.filteredItemsByCategory.visitor.length,
            };
        },
        filteredAll () {
            return [
                ...this.filteredItemsByCategory.account,
                ...this.filteredApps,
                ...this.filteredFeatures,
                ...this.filteredGroups,
                ...this.filteredPages,
                ...this.filteredSegments,
                ...this.filteredTrackEvents,
                ...this.filteredItemsByCategory.visitor,
            ];
        },
        filteredApps () {
            return this.query ? this.allApps.filter(itemFilter(this.query)) : this.allApps;
        },
        filteredGroups () {
            return this.query ? this.allGroups.filter(itemFilter(this.query)) : this.allGroups;
        },
        filteredFeatures () {
            return this.query ? this.allFeatures.filter(itemFilter(this.query)) : this.allFeatures;
        },
        filteredPages () {
            return this.query ? this.allPages.filter(itemFilter(this.query)) : this.allPages;
        },
        filteredSegments () {
            return this.query ? this.allSegments.filter(itemFilter(this.query)) : this.allSegments;
        },
        filteredTrackEvents () {
            return this.query ? this.allTrackEvents.filter(itemFilter(this.query)) : this.allTrackEvents;
        },
        isActiveCategoryLimitedResults () {
            const filterableCategories = Object.keys(this.filteredItemsByCategory);
            const isFilterableCategory = filterableCategories.includes(this.categoryActive);
            // This value comes from aggs used to fetch accounts/visitors
            const areResultsFiltered = this.countByCategory[this.categoryActive] >= 100;

            return isFilterableCategory && areResultsFiltered;
        },
        visibleItems () {
            switch (this.categoryActive) {
                case 'account':
                    return this.filteredItemsByCategory.account;
                case 'all':
                    return this.filteredAll;
                case 'app':
                    return this.filteredApps;
                case 'feature':
                    return this.filteredFeatures;
                case 'page':
                    return this.filteredPages;
                case 'productArea':
                    return this.filteredGroups;
                case 'segment':
                    return this.filteredSegments;
                case 'trackEvent':
                    return this.filteredTrackEvents;
                case 'visitor':
                    return this.filteredItemsByCategory.visitor;
            }

            return [];
        },
    },
    watch: {
        query: {
            immediate: true,
            handler () {
                this.filteredItemsByCategory.account = [];
                this.filteredItemsByCategory.visitor = [];
                this.setCategoryLoadingState('account', LOADING);
                this.setCategoryLoadingState('visitor', LOADING);
            },
        },
        'inactiveVisibleByCategory.account': function () {
            this.filteredItemsByCategory.account = [];
            this.setCategoryLoadingState('account', LOADING);
        },
        'inactiveVisibleByCategory.visitor': function () {
            this.filteredItemsByCategory.visitor = [];
            this.setCategoryLoadingState('visitor', LOADING);
        },
    },
    mounted () {
        this.setOnKeyDownHandler(this.onKeyDown);
        this.loadAllGroups({ items: true });

        const accountSearch$ = connectAggregationToState(
            MentionsSearchAccount,
            withComponent({
                query: 'query',
                showInactive: 'inactiveVisibleByCategory.account',
            }),
        )({
            component: this,
        }, {
            debounceRequests: true,
        });

        const visitorSearch$ = connectAggregationToState(
            MentionsSearchVisitor,
            withComponent({
                query: 'query',
                showInactive: 'inactiveVisibleByCategory.visitor',
            }),
        )({
            component: this,
        }, {
            debounceRequests: true,
        });

        this.subscriptionAccountSearch = accountSearch$.subscribe(({ status, value }) => this.onAggResponse('account', status, value));
        this.subscriptionVisitorSearch = visitorSearch$.subscribe(({ status, value }) => this.onAggResponse('visitor', status, value));
    },
    beforeUnmount () {
        this.subscriptionAccountSearch?.unsubscribe();
        this.subscriptionVisitorSearch?.unsubscribe();
    },
    methods: {
        ...mapActions('groups', { loadAllGroups: 'loadAll' }),
        onAggResponse (category, status, data) {
            this.setCategoryLoadingState(category, status);
            if (status === RESOLVED) {
                this.filteredItemsByCategory[category] = data.map(normalizeItem(category));
            }
        },
        onCategoryEnter (category) {
            this.categoryActive = category;
            this.selectPaneRight();
        },
        onCategorySelect (category) {
            this.categoryActive = category;
        },
        onKeyDown (event) {
            if (event.key === 'ArrowLeft') {
                this.selectPaneLeft();

                return true;
            }
            if (event.key === 'ArrowRight') {
                this.selectPaneRight();

                return true;
            }
            if (event.key === 'Tab') {
                if (event.shiftKey) {
                    this.selectPaneLeft();
                } else {
                    this.selectPaneRight();
                }

                return true;
            }

            return this.activePaneRef?.onKeyDown(event);
        },
        onSelectItem (item) {
            const metadata = {
                id: item.id,
                type: item.type,
                text: getItemText(item),
                ...(item.data.appId != null && { appId: item.data.appId }),
            };
            this.command({ id: item.id, label: JSON.stringify(metadata) });
        },
        onToggleInactive (category) {
            this.inactiveVisibleByCategory[category] = !this.inactiveVisibleByCategory[category];
        },
        selectPane (pane) {
            const index = this.panes.indexOf(pane);
            if (index === -1) return;

            this.paneIndexActive = index;
        },
        selectPaneLeft () {
            if (this.paneIndexActive > 0) {
                this.paneIndexActive -= 1;
            }
        },
        selectPaneRight () {
            if (this.paneIndexActive < this.panes.length - 1) {
                this.paneIndexActive += 1;
            }
        },
        setCategoryLoadingState (category, state) {
            this.loadingStateByCategory[category] = state;

            const allCategories = without(this.categories, 'all');
            const isCategoryReady = (category) => this.loadingStateByCategory[category] !== LOADING;
            const allCategoriesReady = every(allCategories, isCategoryReady);

            this.loadingStateByCategory.all = allCategoriesReady ? RESOLVED : LOADING;
        },
    },
};
</script>

<style scoped lang="less">
.mentions-popover {
    display: flex;
    background: var(--palette-gray-0);
    box-shadow: 0 4px 4px 0 var(--semantic-drop-shadow-base);
    border: 1px solid var(--palette-gray-20);
    border-radius: 8px;
    overflow: hidden;
}
</style>
